<template>
  <div>
    <portal target-el="#filter">
      <div class="sub-header">
        <div class="header-item">
          <Breadcrumb type="flex" separator-class="el-icon-arrow-right">
            <breadcrumb-item v-if="orderDetails && !isOrderLoading">
              <span @click="routeToIndentList">{{
                orderDetails.order_number
                }}</span></breadcrumb-item>
            <breadcrumb-item>{{ $t('INDENT_DOCUMENTS') }}</breadcrumb-item>
          </Breadcrumb>
        </div>
        <div class="header-item f-right" v-if="is3dVisualTabSelected">
          <Button type="primary" @click="downloadLoadPlan">{{
            $t('INDENT_LOAD_PLAN_3D')
            }}</Button>
        </div>
        <div class="header-item f-right" v-if="isMaterialTabSelected && orderDetails && !isOrderLoading">
          <div class="btn-inline">
            <Button type="secondary" v-if="showEditDeliveryNumbers" @click="saveDeliveryNumber">{{ $t('INDENT_SAVE')
            }}</Button>
            <template v-if="isLrExtractionEnabled">
              <template v-if="!indentSummaryStatus">
                <Button type="secondary" class="gradient-button" @click="generateSummary">
                  <label>
                    <span>GET SUMMARY</span>
                  </label>
                </Button>
              </template>
              <template v-else-if="indentSummaryStatus === 'PROGRESS'">
                <Button type="secondary" class="gradient-button">
                  <label>
                    <span class="arc-loader"></span>
                    <span>{{ $t('POD_AI_SUMMARY_PROCESSING') }}</span>
                  </label>
                </Button>
              </template>
              <template v-if="indentSummaryStatus === 'COMPLETED'">
                <Button type="secondary" class="gradient-button" @click="viewLRSummary">
                  <label>
                    <span>{{ $t('POD_AI_SUMMARY') }}</span>
                  </label>
                </Button>
              </template>
            </template>
            <template v-if="!hasMaterialInvoice && !uploadMaterialCheck">
              <Button type="secondary" v-permission="['indent_upload_material']">
                <label>
                  <input v-if="isUploadInProcess" class="upload-material" type="file"
                    :accept="`${AttachmentConstants.excelMimes}`" v-on:change="uploadMaterial" />
                  <input else class="upload-material" type="file" :accept="`${AttachmentConstants.excelMimes}`"
                    v-on:change="uploadMaterial" />
                  {{ $t('INDENT_UPLOAD') }}
                </label>
              </Button>
            </template>
            <Button type="primary" v-if="!isDispatched" @click="loadPlan">{{
              $t('INDENT_LOAD_PLAN')
              }}</Button>
            <Dropdown trigger="click" v-if="$hasPermission('indent_export_materials')">
              <Button type="secondary" class="btn-dropdown">
                {{ $t('INDENT_DOWNLOAD') }}
                <i class="el-icon-arrow-down el-icon--right"></i>
              </Button>
              <Dropdown-menu slot="dropdown">
                <Dropdown-item @click.native="downloadMaterial">
                  {{ $t('INDENT_MATERIALS_DOWNLOAD') }}
                </Dropdown-item>
                <Dropdown-item v-if="isExportWithEpod" @click.native="downloadMaterial(true)">
                  {{ $t('INDENT_DOWNLOAD_WITH_EPOD') }}
                </Dropdown-item>
                <Dropdown-item @click.native="downloadMaterialComparison" v-if="$hasFeature('material_comparison_export')">
                  {{ $t('INDENT_MATERIALS_COMPARION_DOWNLOAD') }}
                </Dropdown-item>
                <Dropdown-item @click.native="downloadIndentDetails" v-if="$hasFeature('indent_details_download')">
                  {{ $t('INDENT_DETAILS_DOWNLOAD') }}
                </Dropdown-item>
              </Dropdown-menu>
            </Dropdown>
            <Button type="primary" v-if="$hasPermission('trigger_epod') &&
                isMaterialTabSelected &&
                showReTriggerEPOD &&
                epod_not_clicked
                " @click="retriggerEpod">{{ $t('INDENT_RETRIGGER_EPOD') }}</Button>
            <Button type="primary" v-if="$hasPermission('trigger_zpod') &&
              isMaterialTabSelected && showTriggerZPOD
              && zpod_not_clicked" @click="triggerZpod">{{ $t('INDENT_TRIGGER_ZPOD') }}</Button>
            <Button type="primary" v-else-if="showGrn && isMaterialTabSelected &&
              editGrn && shipperApprovalPresent && !isCleanEpodApplicable" @click="approve" :disabled="disableApprove">{{ $t('INDENT_APPROVE')
              }}</Button>
            <Button v-if="orderDetails.show_epod_approve_reject" class="btn-approve"
              :loading="epodApprovalFlow.approveEpodLoader" type="secondary" plain @click="approveSDCSubmission"><span
                style="color:#67c23a">{{ $t('INDENT_GRN_APPROVE') }}</span></Button>
            <Button v-if="orderDetails.show_epod_approve_reject" class="btn-reject"
              :loading="epodApprovalFlow.rejectEpodLoader" type="secondary" plain @click="showRejectPopover"><span
                style="color:#E25757">{{ $t('INDENT_GRN_REJECT') }}</span></Button>
          </div>
        </div>
      </div>
    </portal>
    <div>
      <Dialog :title="$t('INDENT_GRN_REJECTION')" :visible.sync="showRejctionReasonPopover" width="450px" top="6vh"
        :show-close="true" :before-close="logout" :close-on-click-modal="false" :close-on-press-escape="false"
        custom-class="indent-update-dialog" class="default-dialog">
        <p class="content_text"> {{ $t('MDM_REJECTION_MESSAGE_REQUEST') }} </p>
        <div class="dialog-content dialog-scroll">
          <Form class="form-default mr-5">
            <FormItem :label="$t('REASON_TO_REJECT')" :required="true">
              <Input :placeholder="$t('INDENT_ENTER_REASON')" :maxlength="150" :rows="2" type="textarea"
                class="form-control" v-model="epodRejectionReason" />
            </FormItem>
          </Form>
        </div>
        <span slot="footer" class="dialog-footer">
          <Button type="secondary" size="mini" @click="showRejctionReasonPopover = false;">
            {{ $t('CANCEL') }}
          </Button>
          <Button :disabled="disableEpodRejectButton" type="primary" size="mini" @click="rejectSDCSubmission">
            {{ $t('INDENT_REJECT') }}
          </Button>
        </span>
      </Dialog>
      <ElTabs class="tab-default tab-sm text-dark" v-model="currentSelectedTab" @tab-click="setTabChangeGA()">
        <!-- TODO: kaushik move this as common and use in bajaj document-v2 as well -->
        <Tab-pane :label="$t('INDENT_DETAILS')" v-if="showTrackDetails" name="details">
          <track-details :orderDetails="orderDetails" :configurations="configurations" />
        </Tab-pane>
        <Tab-pane :label="$t('INDENT_MATERIAL_EPOD')" name="material">
          <material-epod-tab ref="materialEPODRef" :showGrn="showGrn" :approve="approve" :editGrn="editGrn" :showEditDeliveryNumbers="showEditDeliveryNumbers"
            :legalEntityDetails="legalEntityDetails" :isReturnMaterialsEnabled="isReturnMaterialsEnabled"
            :unsavedReturnMaterialDetails="unsavedReturnMaterialDetails"
            :returnMaterialWarnedOnce="returnMaterialWarnedOnce" @routeToIndentsList="routeToIndentList" @setGA="setGA"
            @epodRejected="epodRejected" @showApproveButton="showApproveButton"
            @disableApproveButton="disableApproveButton"></material-epod-tab>
        </Tab-pane>
        <Tab-pane :label="$t('INDENT_FREIGHT_BIFURCATION')" name="bifurcation" v-if="showFreightWorkingDetails">
          <FreightCalculations selected="bifurcation" :orderDetails="orderDetails" />
        </Tab-pane>
        <Tab-pane :label="$t('INDENT_FREIGHT_CALCULATION')" name="calculation" v-if="showFreightWorkingDetails">
          <FreightCalculations selected="calculation" :orderDetails="orderDetails"
            :disableFreightProvisionRetry="disableFreightProvisionRetry"
            @retry-freight-provision="retryFreightProvision"
          />
        </Tab-pane>

        <Tab-pane
          :label="$t('INDENT_3D_VISUALIZATION')"
          name="visualize"
          v-if="show3dVisualization"
        >
          <Visualize
            :isShowing="currentSelectedTab == 'visualize'"
            :orderDetails="orderDetails"
          />
        </Tab-pane>
        <Tab-pane v-if="$hasFeature('handling_reconciliation')"
          label="Handling Reconciliation" name="reconciliation">
          <HandlingReconciliationTab />
        </Tab-pane>
        <Tab-pane v-if="showPayAnomaly"
          :label="$t('INDENT_PAY_ANOMALY')"
          name="pay-anamoly">
          <pay-anomaly />
        </Tab-pane>
        <Tab-pane label="Attachments" name="attachments">
          <attachments-section />
        </Tab-pane>
      </ElTabs>
    </div>
  </div>
</template>

<script>
/* eslint-disable import/no-extraneous-dependencies */
import { mapGetters } from 'vuex';
import { uniq, uniqBy, omit, isEmpty } from 'lodash';
import { movementType, deliveryPendingReasonsMapping, epodApprovalStatus, statusIdDictionary, LorryReceiptGenerationCategory, InvertedMovements } from 'pando-constants';
import AttachmentConstants from '@common/constants/AttachmentConstants';
import LrNumberListOnConfigMixin from '@common/mixins/document/LrNumberListOnConfigMixin';
import MaterialEpodTab from '@/modules-v2/indent/views/document/material-epod-tab';
import TableCell from '@/components/orders/documentation/TableCell';
import FreightCalculations from '@/components/modules/freight/Page';
import Visualize from '@/components/modules/3d-visualization/visualize';
import { Menu } from '@/constants/order';
import HandlingReconciliationTab from './handling-reconciliation-tab.vue';
import OrderUtils from '../../../../utils/order';
import PayAnomaly from './pay-anomaly';
import TrackDetails from './track-details';
import AttachmentsSection from './attachments-section';

export default {
  components: {
    TableCell,
    FreightCalculations,
    MaterialEpodTab,
    Visualize,
    HandlingReconciliationTab,
    PayAnomaly,
    TrackDetails,
    AttachmentsSection,
  },
  mixins: [LrNumberListOnConfigMixin],
  data() {
    return {
      // constants
      AttachmentConstants,
      Menu,
      isUploadInProcess: false,
      currentSelectedTab: 'material',
      epod_not_clicked: true,
      zpod_not_clicked: true,
      showOtpPopup: false,
      otp: null,
      disableFreightProvisionRetry: false,
      legalEntityDetails: {},
      disableApprove: false,
      epodApprovalFlow: {
        approveEpodLoader: false,
        rejectEpodLoader: false,
      },
      showRejctionReasonPopover: false,
      epodRejectionReason: '',
      indentSummaryStatus: null,
      logout: null,
    };
  },
  async created() {
    this.category = this.$route.params.category;
    this.$store.commit('order/SET_ORDER', {});
    if (this.$hasFeature('view_track_details')) this.currentSelectedTab = 'details';
    await this.loadLegalEntity();
    if (this.$hasFeature('lr_extraction')) {
      await this.getLRSummary();
    }
  },
  computed: {
    ...mapGetters({
      orderDetails: 'order/find',
      isOrderLoading: 'order/isOrderLoading',
      configurations: 'app/configurations',
      filter: 'order/filter',

      isMaterialLoading: 'order/isMaterialLoading',
      allMaterials: 'order/materials',
      materialHeaders: 'order/materialHeaders',

      isLrLoading: 'order/isLrLoading',
      lr: 'order/lr',
      grnDetails: 'order/grnDetails',
      userEdited: 'order/userEdited',
      warnedOnce: 'order/warnedOnce',
      depots: 'app/otherDepots',

      deliveryDetails: 'order/deliveryNumberDetails',
      unsavedReturnMaterialDetails: 'order/returnMaterialDetails',
      returnMaterialWarnedOnce: 'order/returnMaterialWarnedOnce',
      hasUnsavedComplaints: 'order/hasUnsavedComplaints',
      unsavedComplaintsWarnedOnce: 'order/unsavedComplaintsWarnedOnce',
    }),
    isLrExtractionEnabled() {
      return this.$hasFeature('lr_extraction');
    },
    showComplaints() {
      return this.$hasFeature('customer_complaints') && (
        this.$hasPermission('view_customer_complaints') || this.$hasPermission('raise_complaints')
      );
    },
    showTrackDetails() {
      return !isEmpty(this.orderDetails) && this.$hasFeature('view_track_details');
    },
    isFromEximList() {
      const isEximRoute = this.$route.query && this.$route.query.isEximRoute;
      return isEximRoute === 'true';
    },
    isExportWithEpod() {
      return this.$hasFeature('is_material_export_with_epod_opted');
    },
    showEditDeliveryNumbers() {
      const isMaterialsPresent =
        this.allMaterials &&
        this.allMaterials.materials &&
        this.allMaterials.materials.length;
      const isConfigPresent =
        this.configurations &&
        this.configurations.delivery_number_without_material_enabled;
      const isDeliveryDetailsPresent =
        this.orderDetails &&
        this.orderDetails.delivery_numbers &&
        this.orderDetails.delivery_numbers.length;
      return (
        !isMaterialsPresent && isConfigPresent && !isDeliveryDetailsPresent
      );
    },
    routeToIndentPath() {
      if (this.isFromEximList) {
        return { name: 'EximMasterIndent' };
      } else if (localStorage.getItem('controlTowerQueryParams')) {
        const controlTowerParams = JSON.parse(
          localStorage.getItem('controlTowerQueryParams'),
        );
        if (parseInt(this.category, 10) === 1) {
          return {
            name: 'InboundIndentsV2',
            query: {
              metricType: controlTowerParams.metricType,
              depotId: controlTowerParams.depotId,
            },
          };
        }
        return {
          name: 'OutboundIndentsV2',
          query: {
            metricType: controlTowerParams.metricType,
            depotId: controlTowerParams.depotId,
          },
        };
      }
      if (parseInt(this.category, 10) === 1) {
        return { name: 'InboundIndentsV2' };
      }
      return { name: 'OutboundIndentsV2' };
    },
    uploadMaterialCheck() {
      const orderStatus = this.orderDetails.status;
      const hasPermission = this.$hasPermission('upload_material_after_dispatch');
      const statusCheck = hasPermission
        ? (this.$statusUtil.isDelivered(orderStatus))
        : (this.$statusUtil.isDispatched(orderStatus) || this.$statusUtil.isAfterDispatched(orderStatus));
      const uploadConfig = this.configurations
        && this.configurations.indentConfig
        && this.configurations.indentConfig.upload_material_after_dispatch_block;
      const movementKey = InvertedMovements[this.orderDetails.movement];
      const isDispatchedOrAfter = this.$statusUtil.isDispatched(orderStatus)
        || this.$statusUtil.isAfterDispatched(orderStatus);
      const isConfigBlocked = !!uploadConfig
        && uploadConfig[movementKey] === true
        && isDispatchedOrAfter;

      return statusCheck || isConfigBlocked;
    },
    isDispatched() {
      const orderStatus = this.orderDetails.status;
      return (
        this.$statusUtil.isDispatched(orderStatus) ||
        this.$statusUtil.isAfterDispatched(orderStatus)
      );
    },
    showFreightWorkingDetails() {
      const checkDelivered =
        this.orderDetails.status &&
        ['delivered', 'delivery_pending', 'dispatched'].includes(
          this.$statusUtil.getMainStatusName(this.orderDetails.status),
        );
      const hideFreightSettings =
        this.configurations.payment_settings &&
        this.configurations.payment_settings.hide_freight_working;
      return (
        this.orderDetails.order_type !== 2 &&
        checkDelivered &&
        !hideFreightSettings
      );
    },
    show3dVisualization() {
      return this.configurations && this.configurations.visualization_3d;
    },
    isMaterialTabSelected() {
      return this.currentSelectedTab === 'material';
    },
    is3dVisualTabSelected() {
      return this.currentSelectedTab === 'visualize';
    },
    /**
     * start of buttons and config check
     */
    showReTriggerEPOD() {
      if (
        this.configurations.erpEndPoint &&
        this.orderDetails.status === 23 &&
        this.orderDetails.delivery_pending_reason ===
        deliveryPendingReasonsMapping.EPOD_NA
      ) {
        return true;
      }
      return false;
    },
    showTriggerZPOD() {
      const orderStatus = this.orderDetails.status;
      return this.hasMaterialInvoice && orderStatus && (
        !(['cancelled', 'delivered'].includes(
          this.$statusUtil.getMainStatusName(orderStatus))));
    },
    isDeliveredIndent() {
      return this.orderDetails.status === 7;
    },
    isCancelledIndent() {
      return this.orderDetails.status === 8;
    },
    showPayAnomaly() {
      return (this.isDeliveredIndent || this.isCancelledIndent) && !this.$hasFeature('no_pay');
    },
    showGrn() {
      if (this.inboundShowEditGRN || this.orderDetails.status === 40) {
        return true;
      } else if (
        this.orderDetails.status === 23 ||
        this.orderDetails.status === 7 ||
        this.orderDetails.status === 6
      ) {
        return (
          this.orderDetails &&
          this.orderDetails.destinations &&
          this.orderDetails.destinations.some(
            destination => destination.release_at,
          )
        );
      }
      return false;
    },
    inboundShowEditGRN() {
      // currently assuming one source
      if (this.category && this.category.toString() === '1') {
        // condition for inbound
        if (this.orderDetails.status === 23 || this.orderDetails.status === 6) {
          // inbound we show GRN in Delpending and dispatched
          return true;
        }
      }
      return false;
    },
    editGrn() {
      const indentConfig = this.configurations.indentConfig;
      let editGRN = false;
      const deliveryBlockSettings =
        indentConfig && indentConfig.grn_movement_delivery_block;
      const grnApprovalForTransporter =
        this.configurations.grnApprovalForTranporter;
      if (indentConfig && indentConfig.is_shipper_required_to_fill_grn) {
        let movementBlocked = false;
        if (grnApprovalForTransporter) {
          movementBlocked = true;
        }
        if (deliveryBlockSettings) {
          if (
            this.orderDetails.movement === movementType.PRIMARY &&
            deliveryBlockSettings.primary
          ) {
            movementBlocked = true;
          } else if (
            this.orderDetails.movement === movementType.SECONDARY &&
            deliveryBlockSettings.secondary
          ) {
            movementBlocked = true;
          } else if (
            this.orderDetails.movement === movementType.INWARD &&
            deliveryBlockSettings.vendor
          ) {
            movementBlocked = true;
          } else if (
            this.orderDetails.movement === movementType.RETURNS &&
            deliveryBlockSettings.return
          ) {
            movementBlocked = true;
          }
          if (movementBlocked) {
            editGRN = this.orderDetails.status === 23;
          }
        }
        editGRN = editGRN || this.inboundShowEditGRN;
      }
      // based on approval status we will override all existing grn editability check
      editGRN = OrderUtils.grnEditabilityCheckBasedOnApprovalStatus(
        this.orderDetails,
        editGRN,
      );
      return editGRN;
    },

    isCleanEpodApplicable() {
      if (!this.orderDetails.is_clean_epod_applicable) return false;
      const materials = this.allMaterials && this.allMaterials.materials;
      return OrderUtils.hasZeroSDC(materials);
    },
    shipperApprovalPresent() {
      if (
        this.$hasFeature('grn_shipper_approval') &&
        this.$hasPermission('grn_approval') &&
        this.orderDetails.status === 23 &&
        this.orderDetails.epod_approval_status === epodApprovalStatus.PENDING
        // only when approval event is created 'approve' button will not be visible
      ) {
        return true;
      }
      return false;
    },

    canEnterOtp() {
      if (this.category === 1) {
        // condition for inbound
        return (
          this.$hasFeature('epod_otp_verification') &&
          this.orderDetails.status === 23 &&
          this.orderDetails.delivery_pending_reason ===
          deliveryPendingReasonsMapping.OTP_VERIFICATION
        );
      }
      return false;
    },
    showMaterialComparisonDownload() {
      return this.$hasFeature('material_comparison_export') && this.$hasPermission('indent_export_materials');
    },
    isReturnMaterialsEnabled() {
      return this.$hasFeature('return_management') && this.$hasPermission('create_return_deliveries');
    },
    hasMaterialInvoice() {
      const hasInvoice =
        this.allMaterials &&
        this.allMaterials.materials.some(material => material.invoice.number);
      return (
        hasInvoice && this.$hasFeature('avoid_manual_material_invoice_update')
      );
    },
    disableEpodRejectButton() {
      let result = true;
      if (this.epodRejectionReason && this.epodRejectionReason.trim().length) {
        result = false;
      }
      return result;
    },
    /**
     * end of buttons and config check
     */
  },
  methods: {
    async generateSummary() {
      const orderNo = this.$route.params.orderId;
      const result = await this.$store.dispatch('order/generateLRSummary', { orderNo });
      if (result.status === 202) {
        this.indentSummaryStatus = 'PROGRESS';
        await this.getLRSummary(); // start polling for status
        this.$success({ message: 'Summary fetch started' });
      } else {
        this.$error({ message: result.data.message });
      }
    },
    async getLRSummary() {
      const orderNo = this.$route.params.orderId;
      const result = await this.$store.dispatch('order/getLRSummary', orderNo);
      const statusCode = result.data && result.data.code;
      this.indentSummaryStatus = statusCode;
      if (statusCode === 'PROGRESS') {
        setTimeout(() => {
          this.getLRSummary();
        }, 3000);
      } else if (statusCode === 'COMPLETED') {
        this.indentSummaryStatus = statusCode;
      } else if (statusCode === 'FAILED') {
        this.indentSummaryStatus = null;
        const errorData = result.data;
        const errorMessage = errorData.message || 'OCR summary generation failed';
        const errorType = errorData.errorType || 'PROCESSING_ERROR';
        const displayMessage = errorType === 'AI_SERVICE_ERROR'
          ? `AI Service Error: ${errorMessage}`
          : `Processing Error: ${errorMessage}`;
        this.$error({ message: displayMessage });
      }
    },
    viewLRSummary() {
      this.$refs.materialEPODRef.openLrSummarySlider();
    },
    async approveSDCSubmission() {
      this.epodApprovalFlow.approveEpodLoader = true;
      const orderNo = this.$route.params.orderId;
      try {
        const result = await this.$store.dispatch('order/approveSDCSubmission', { orderNo });
        if (result && result.message) {
          this.$successMessage({ message: result.message });
          this.routeToIndentList();
        } else {
          this.$errorMessage({ message: 'Something went wrong. Please try again later' });
        }
      } catch (err) {
        this.$errorMessage({ message: this.$getErr('APP_CATCH_ERR') });
      }
      this.epodApprovalFlow.approveEpodLoader = false;
    },
    showRejectPopover() {
      this.showRejctionReasonPopover = true;
    },
    async rejectSDCSubmission() {
      this.showRejctionReasonPopover = false;
      this.epodApprovalFlow.rejectEpodLoader = true;
      const orderNo = this.$route.params.orderId;
      try {
        const result = await this.$store.dispatch('order/rejectSDCSubmission',
          { orderNo, reason: this.epodRejectionReason });
        if (result && result.message) {
          this.$successMessage({ message: result.message });
          this.routeToIndentList();
        } else {
          this.$errorMessage({ message: 'Something went wrong. Please try again later' });
        }
      } catch (err) {
        this.$errorMessage({ message: this.$getErr('APP_CATCH_ERR') });
      }
      this.epodApprovalFlow.rejectEpodLoader = false;
    },
    async loadLegalEntity() {
      try {
        const result = await this.$store.dispatch('order/getLegalEntity', {
          orderNo: this.$route.params.orderId,
        });
        if (!result || result.error) {
          this.$errorMessage({ message: 'Cannot load legal entity' });
        } else {
          this.legalEntityDetails = result;
        }
      } catch (err) {
        this.$errorMessage({ message: this.$getErr('APP_CATCH_ERR') });
      }
    },
    async retryFreightProvision() {
      try {
        this.disableFreightProvisionRetry = true;
        const result = await this.$store.dispatch(
          'order/triggerFreightProvision',
          { orderId: this.orderDetails.order_number },
        );
        if (result.status === 'Success') {
          this.$success({
            message: this.$getSuccess('FREIGHT_PROVISION_SUCCESS'),
          });
        } else {
          this.$error({ message: this.$getErr('RETRY_FAILED') });
        }
      } catch (error) {
        this.$error({ message: this.$t('APP_CATCH_ERR') });
      }
    },
    routeToIndentList() {
      if (this.$route.params.isListView) {
        if (this.$route.params.category === 1) {
          this.$router.push({ name: 'InboundIndents' });
        } else {
          this.$router.push({ name: 'OutboundIndents' });
        }
      } else if (this.$route.params.isSwimLane) {
        if (this.$route.params.category === 1) {
          this.$router.push({ name: 'InboundIndentsV2' });
        } else {
          this.$router.push({ name: 'OutboundIndentsV2' });
        }
      } else if (
        this.isReturnMaterialsEnabled
        && !isEmpty(this.unsavedReturnMaterialDetails) && !this.returnMaterialWarnedOnce
      ) {
        this.$warningMessage({ message: this.$t('RETURN_MATERIALS_UNSAVED_WARNING') });
        this.$store.commit('order/RETURN_MATERIALS_WARNED_ONCE', true);
      } else if (this.userEdited && !this.warnedOnce) {
        this.$warningMessage({ message: this.$getConst('EPOD_VALUE_CHANGED') });
        this.$store.commit('order/HAS_WARNED_ONCE', true);
      } else if (this.showComplaints && this.hasUnsavedComplaints &&
        !this.unsavedComplaintsWarnedOnce) {
        this.$warningMessage({ message: this.$t('UNSAVED_COMPLAINTS_WARNING') });
        this.$store.commit('order/UNSAVED_COMPLAINTS_WARNED_ONCE', true);
      } else {
        this.$router.push(this.routeToIndentPath);
      }
    },
    epodRejected() {
      this.disableApprove = true;
    },
    showApproveButton() {
      this.disableApprove = false;
    },
    disableApproveButton() {
      this.disableApprove = true;
    },
    /**
     * start of actions of buttons in material details tab
     */
    loadPlan() {
      const orderId = this.orderDetails.order_number;
      this.setGA('Click', 'Load Plan Button');
      this.$store.dispatch('order/loadPlan', orderId);
    },

    async uploadMaterial(event) {
      const attachment = event.target.files[0];
      const orderId = this.orderDetails.order_number;
      try {
        this.setGA('Click', 'Upload Button');
        const result = await this.$store.dispatch('order/uploadMaterials', {
          orderId,
          attachment,
        });
        if (result.error) {
          this.$error({ message: result.error });
        } else {
          if (result.valid_count >= 0) {
            await this.$store.dispatch('order/materials', {
              orderId: this.$route.params.orderId,
            });
            if (this.configurations && this.configurations.manual_lr === true
              && this.configurations.lorry_receipt_generate_category ===
              LorryReceiptGenerationCategory.CONSIGNEE_WISE
            ) {
              await this.$store.dispatch('order/getUpdatedOrder', {
                orderId: this.$route.params.orderId,
              });
              await this.$store.dispatch(
                'order/lorryReceipt',
                this.$route.params.orderId,
              );
            }
            this.isUploadInProcess = !this.isUploadInProcess;
          }
          if (result.valid_count === 0) {
            this.$error({
              message: `${result.valid_count} materials uploaded <br/> Unknown Materials count${result.invalid_count}`,
              duration: 30000,
            });
          } else if (result.invalid_count > 0) {
            this.$warningMessage({
              message: `${result.valid_count} materials uploaded <br/> Unknown Materials count ${result.invalid_count}`,
              duration: 30000,
            });
          } else {
            this.$success({
              message: `All ${result.valid_count} materials uploaded successfully`,
            });
          }
        }
      } catch (error) {
        this.$error({
          message: 'Something went wrong! please try after some time',
        });
      }
    },

    downloadMaterial(isWithEpod = false) {
      this.setGA('Click', 'Download Button');
      const orderId = this.orderDetails.order_number;
      this.$store.dispatch('order/downloadMaterial', { orderId, isWithEpod });
    },

    downloadMaterialComparison() {
      this.selectedDownloadType = 'material_comparison_download';
      this.setGA('Click', 'Download Material Comparison');
      const orderId = this.orderDetails.order_number;
      this.$store.dispatch('order/downloadMaterialComparison', orderId);
    },

    downloadIndentDetails() {
      this.setGA('Click', 'Download Indent Details');
      const orderId = this.orderDetails.order_number;
      this.$store.dispatch('order/downloadIndentDetails', orderId);
    },

    downloadLoadPlan() {
      this.setGA('Click', 'Download Button');
      const orderId = this.orderDetails.order_number;
      this.$store.dispatch('order/downloadLoadPlan', orderId);
    },

    async retriggerEpod() {
      try {
        this.setGA('Click', 'Re-Trigger ePOD');
        const res = await this.$store.dispatch('order/retriggerEpod', {
          orderNo: this.orderDetails.order_number,
        });
        if (res.status === 204) {
          this.$success({ message: 'EPOD TRIGGERED SUCCESSFULLY' });
          this.epod_not_clicked = false;
        } else {
          this.$error({ message: this.$getErr('RETRIGGER_EPOD_FAILED') });
        }
      } catch (err) {
        this.$error({ message: this.$getErr('RETRIGGER_EPOD_FAILED') });
      }
    },

    async triggerZpod() {
      try {
        this.setGA('Click', 'Trigger ZPOD');
        const res = await this.$store.dispatch('order/triggerZpod', {
          orderNo: this.orderDetails.order_number,
        });
        if (res.status === 204) {
          this.$success({ message: this.$t('INDENT_ZPOD_TRIGGERED') });
          this.zpod_not_clicked = false;
        } else {
          this.$error({ message: this.$getErr('TRIGGER_ZPOD_FAILED') });
        }
      } catch (err) {
        this.$error({ message: this.$getErr('TRIGGER_ZPOD_FAILED') });
      }
    },

    async approve() {
      try {
        this.setGA('Click', 'Approve Button');
        let orderConsignees = uniqBy(
          this.orderDetails.destinations,
          'location_id',
        );
        orderConsignees = orderConsignees.filter(
          consignee => consignee.release_at,
        );
        const promptText = this.$getConst('GRN_SUBMIT_PROMPT', {
          firstArgument: orderConsignees.length,
        });
        const buttontext = 'Approve';
        const confirmApprove = await this.confirmationBox(
          promptText,
          buttontext,
        );
        if (confirmApprove === 'confirm') {
          const orderNo = this.orderDetails.order_number;
          const consigneesIds = this.orderDetails.destinations.map(
            _destination => ({
              destination_id: _destination.location_id,
              ...this.$hasFeature('last_mile') ? { unique_id: _destination.unique_id } : {},
            }),
          );
          const result = await this.$store.dispatch('order/markDelivered', {
            orderNo,
            consigneesIds,
          });
          // triggerIndentStatusUpdate
          await this.$store.dispatch('paymentsV2/updateIndentStatus', {
            id: orderNo,
          });
          if (result.data && result.data.status === statusIdDictionary.delivered) {
            this.$success({ message: this.$getSuccess('GRN_SUBMIT_SUCCESS') });
          } else if (result.error) {
            this.$error({
              message: result.error,
            });
          }
          this.routeToIndentList();
        }
      } catch (err) {
        this.$error({ message: this.$getErr('GRN_SUBMIT_FAILED') });
      }
    },
    async submitOtp() {
      try {
        const orderNo = this.orderDetails.order_number;
        const otp = this.otp;
        const res = await this.$store.dispatch('order/approveOTP', {
          orderNo,
          otp,
        });
        if (res && res.status === 204) {
          this.$success({ message: 'OTP SUBMITTED SUCCESSFULLY' });
          this.routeToIndentList();
        } else {
          this.$error({ message: this.$getErr('OTP_FAILED') });
        }
      } catch (err) {
        this.$error({ message: this.$getErr('OTP_FAILED') });
      }
    },

    async saveDeliveryNumber() {
      try {
        // validate the payload
        const validPayload = this.deliveryDetails.every(
          val =>
            val.source_id &&
            val.delivery_numbers &&
            val.delivery_numbers &&
            ![
              val.delivery_numbers.charAt(0),
              val.delivery_numbers.charAt(val.delivery_numbers.length - 1),
            ].includes(','),
        );
        if (!validPayload) {
          this.$error({ message: this.$getErr('VALIDATION_ERROR') });
          return;
        }

        const orderNo = this.orderDetails.order_number;
        const payload = this.deliveryDetails.map((deliveryDetail) => {
          let detail = { ...deliveryDetail };
          const deliveryNumbersList =
            detail.delivery_numbers &&
            detail.delivery_numbers.split(',').map(val => val.trim());
          detail.delivery_numbers = uniq(deliveryNumbersList);
          detail = omit(detail, ['location_id']);
          return detail;
        });
        const result = await this.$store.dispatch(
          'order/setDeliveryNumberDetails',
          {
            orderNo,
            payload,
          },
        );
        if (result && !result.error) {
          this.$success({
            message: this.$getSuccess('DELIVERY_NUMBER_UPDATE_SUCCESS'),
          });
          const orderDetails = { ...this.orderDetails };
          orderDetails.delivery_numbers = result && result.delivery_numbers;
          this.$store.commit('order/SET_ORDER', orderDetails);
        } else {
          this.$error({
            message:
              (result && result.error) ||
              this.$getErr('DELIVERY_NUMBER_UPDATE_FAILURE'),
          });
        }
      } catch (err) {
        this.$error({
          message: this.$getErr('DELIVERY_NUMBER_UPDATE_FAILURE'),
        });
      }
    },
    /**
     * end of actions of buttons in material details tab
     */

    async confirmationBox(text, buttonText) {
      return this.$PromptConfirm(text, 'Confirmation', {
        confirmButtonText: buttonText,
        showCancelButton: true,
        lockScroll: true,
        showClose: false,
        center: true,
      });
    },
    setTabChangeGA() {
      if (this.currentSelectedTab === 'bifurcation') {
        this.setGA('Click', 'Freight Bifurcation');
      } else if (this.currentSelectedTab === 'calculation') {
        this.setGA('Click', 'Freight Calculation');
      }
    },
    setGA(action, label) {
      this.$addGAEvent('Indent Management', action, `Documents:${label}`);
    },
  },
};
</script>

<style lang="scss" scoped>
.el-dropdown-menu__item {
  padding: 10px 10px 3px;
}

.btn-reject {
  padding: 0 10px !important;
  color: #E25757;
  border: 1px solid transparent;
}

.btn-reject:hover {
  border-color: #E25757;
}

.btn-approve {
  padding: 0 10px !important;
  color: #67c23a;
  border: 1px solid transparent;
}

.btn-approve:hover {
  border-color: #67c23a;
}

.indent-update-dialog {
  position: fixed;
  right: 0;
}

.gradient-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  background: linear-gradient(to right, #343440, #371A37); /* Blue to Purple */
  color: white;
  border: none;
  cursor: pointer;
  overflow: hidden;
  z-index: 1;
  transition: all 0.3s ease;

  label{
    color: #fff !important;
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    padding: 2px;
    background: linear-gradient(to right, #343440, #371A37);
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: destination-out;
    mask-composite: exclude;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    background: transparent;
  }

  &:hover span {
    background: linear-gradient(to right, #343440, #371A37);
    -webkit-background-clip: text;
    -webkit-text-fill-color: #000;
    background-clip: text;
    color: #000;
  }

  &:hover::before {
    opacity: 1;
  }

  .arc-loader {
    width: 12px;
    height: 12px;
    border: 1px solid #d1c8c8;
    border-left-color:#3b1e58 ;
    border-radius: 50%;
    animation: spin 0.5s linear infinite;
    display: inline-flex;
    margin-top: 1px;

    :hover {
      border-left-color: white;
    }
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>

